import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Ingredient } from 'public-contracts';
import { ingredientsService } from '../services/ingredientsService';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Beaker, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../utils';

interface IngredientComboboxProps {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowNone?: boolean;
  noneLabel?: string;
  'data-testid'?: string;
}

export const IngredientCombobox: React.FC<IngredientComboboxProps> = ({
  value,
  onValueChange,
  placeholder,
  disabled = false,
  className,
  allowNone = false,
  noneLabel,
  'data-testid': dataTestId,
}) => {
  const { translate } = useTranslation();
  const [open, setOpen] = useState(false);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const loadIngredients = React.useCallback(async () => {
    try {
      setIsLoading(true);
      // Load all ingredients - Command component will handle client-side filtering
      const data = await ingredientsService.getIngredients({
        page: 1,
        size: 1000, // Fetch all ingredients
      });
      setIngredients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load ingredients:', err);
      setIngredients([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load all ingredients when popover opens (only once)
  useEffect(() => {
    if (open && ingredients.length === 0) {
      loadIngredients();
    }
  }, [open, ingredients.length, loadIngredients]);

  // Auto-focus search input when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const selectedIngredient = ingredients.find((ing) => ing.id === value);

  const handleSelect = (selectedValue: string) => {
    if (selectedValue === '__none__') {
      onValueChange('');
    } else {
      onValueChange(selectedValue);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between', className)}
          disabled={disabled}
          data-testid={dataTestId}
        >
          <span className="flex items-center gap-2 truncate">
            {value && selectedIngredient ? (
              <>
                <Beaker className="h-4 w-4 shrink-0" />
                <span className="truncate">{selectedIngredient.name}</span>
              </>
            ) : allowNone && !value ? (
              <span className="text-muted-foreground">
                {noneLabel ||
                  translate(
                    'pos.inventory.ingredient.none',
                    'No ingredient'
                  )}
              </span>
            ) : (
              <span className="text-muted-foreground">
                {placeholder ||
                  translate(
                    'pos.recipes.placeholders.selectIngredient',
                    'Select ingredient'
                  )}
              </span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[400px] p-0"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 0);
        }}
      >
        <Command>
          <CommandInput
            ref={searchInputRef}
            placeholder={translate(
              'pos.recipes.placeholders.searchIngredient',
              'Search ingredients...'
            )}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? translate('common.loading', 'Loading...')
                : translate('common.noResults', 'No ingredients found.')}
            </CommandEmpty>
            <CommandGroup>
              {allowNone && (
                <CommandItem
                  value="__none__"
                  onSelect={() => handleSelect('__none__')}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      !value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="text-muted-foreground">
                    {noneLabel ||
                      translate(
                        'pos.inventory.ingredient.none',
                        'No ingredient'
                      )}
                  </span>
                </CommandItem>
              )}
              {ingredients.map((ingredient) => (
                <CommandItem
                  key={ingredient.id}
                  value={ingredient.name}
                  onSelect={() => handleSelect(ingredient.id)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === ingredient.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex items-center space-x-2">
                    <Beaker className="h-4 w-4" />
                    <span>{ingredient.name}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

